import React, { useState, useEffect } from 'react';
import { Building, FileText, Calculator, Database, Award, ChevronRight, Save, Download, AlertCircle, CheckCircle2, Upload } from 'lucide-react';
import PCDBSearch, { PCDBHeatingSystem } from '../PCDBSearch';
import { generateEPCXML, validateEPCData, calculateSAPRating, EPCFormData, SAPRating } from '../../utils/epcGenerator';
import type { User } from '@supabase/supabase-js';

interface EPCDataCollectionProps {
  onComplete: (data: EPCFormData, xmlOutput: string) => void;
  initialData?: Partial<EPCFormData>;
  siteNotesData?: any;
  currentUser: User | null;
}

const EPCDataCollection: React.FC<EPCDataCollectionProps> = ({
  onComplete,
  initialData,
  siteNotesData,
  currentUser
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<EPCFormData>({
    reportReference: '',
    assessorName: '',
    assessorNumber: '',
    assessorCompany: '',
    assessmentDate: new Date().toISOString().split('T')[0],
    reportLanguage: 'EN',
    relatedPartyDisclosure: '1',
    
    uprn: '',
    address: {
      line1: '',
      town: '',
      postcode: ''
    },
    
    transactionType: '1',
    tenure: '1',
    inspectionDate: new Date().toISOString().split('T')[0],
    registrationDate: new Date().toISOString().split('T')[0],
    epcRecommendationCode: '1',
    
    builtForm: '1',
    extensionsCount: 0,
    habitableRoomCount: 0,
    heatedRoomCount: 0,
    measurementType: '1',
    propertyType: '0',
    
    lowEnergyFixedLighting: 0,
    incandescentFixedLighting: 0,
    ledFixedLighting: 0,
    cflFixedLighting: 0,
    
    mechanicalVentilation: '0',
    wetRoomsCount: 0,
    extractFansCount: 0,
    psvCount: 0,
    
    pressureTest: '3',
    hasDraughtLobby: false,
    
    openChimneysCount: 0,
    blockedChimneysCount: 0,
    openFluesCount: 0,
    closedFluesCount: 0,
    boilerFluesCount: 0,
    otherFluesCount: 0,
    fluelessGasFiresCount: 0,
    
    mainHeatingSystem: null,
    
    secondaryHeatingType: '693',
    secondaryHeatingFuel: '0',
    
    hotWaterType: '901',
    hotWaterFuel: '1',
    hotWaterEfficiency: 80,
    solarWaterHeating: false,
    
    walls: [],
    floors: [],
    roofs: [],
    windows: [],
    renewableEnergySources: [],
    
    ...initialData
  });

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedXML, setGeneratedXML] = useState<string>('');
  const [sapRating, setSapRating] = useState<SAPRating | null>(null);

  const steps = [
    { id: 'assessor', title: 'Assessor Details', icon: Award },
    { id: 'property', title: 'Property Details', icon: Building },
    { id: 'heating', title: 'Heating Systems', icon: Database },
    { id: 'fabric', title: 'Building Fabric', icon: Building },
    { id: 'ventilation', title: 'Ventilation', icon: FileText },
    { id: 'review', title: 'Review & Generate', icon: Calculator }
  ];

  // Auto-populate assessor details from user settings
  useEffect(() => {
    if (currentUser?.user_metadata) {
      const firstName = currentUser.user_metadata.first_name || '';
      const lastName = currentUser.user_metadata.last_name || '';
      const fullName = currentUser.user_metadata.assessor_full_name || `${firstName} ${lastName}`.trim();
      
      setFormData(prev => ({
        ...prev,
        assessorName: fullName,
        assessorNumber: currentUser.user_metadata.assessor_id || '',
        assessorCompany: currentUser.user_metadata.assessor_company || ''
      }));
    }
  }, [currentUser]);

  // Auto-populate from site notes data
  useEffect(() => {
    if (siteNotesData) {
      setFormData(prev => ({
        ...prev,
        address: {
          line1: siteNotesData.formData?.address || '',
          town: '',
          postcode: ''
        },
        habitableRoomCount: parseInt(siteNotesData.formData?.habitableRooms || '0'),
        heatedRoomCount: parseInt(siteNotesData.formData?.heatedRooms || '0'),
        lowEnergyFixedLighting: parseInt(siteNotesData.formData?.totalLightsX || '0'),
        incandescentFixedLighting: parseInt(siteNotesData.formData?.totalLightsO || '0'),
        
        // Auto-populate heating system from PCDB if available
        mainHeatingSystem: siteNotesData.selectedPCDBSystem || 
                          (siteNotesData.heatingSystems?.[0]?.pcdbSystem) || null,
        
        // Map building fabric data
        builtForm: siteNotesData.formData?.detachment === 'detached' ? '1' :
                   siteNotesData.formData?.detachment === 'semi-detached' ? '2' :
                   siteNotesData.formData?.detachment === 'terraced' ? '3' : '1',
        
        propertyType: siteNotesData.formData?.detachment === 'flat' ? '1' : '0'
      }));
    }
  }, [siteNotesData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField as keyof EPCFormData] as any,
        [field]: value
      }
    }));
  };

  const validateCurrentStep = (): boolean => {
    const errors: string[] = [];
    
    switch (currentStep) {
      case 0: // Assessor Details
        if (!formData.assessorName) errors.push('Assessor name is required');
        if (!formData.assessorNumber) errors.push('Assessor number is required');
        break;
      case 1: // Property Details
        if (!formData.address.line1) errors.push('Address is required');
        if (!formData.address.postcode) errors.push('Postcode is required');
        break;
      case 2: // Heating Systems
        if (!formData.mainHeatingSystem) errors.push('Main heating system must be selected');
        break;
      // Add more validation as needed
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateEPC = async () => {
    setIsGenerating(true);
    try {
      // Validate all data
      const validation = validateEPCData(formData);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setIsGenerating(false);
        return;
      }

      // Calculate SAP rating
      const rating = await calculateSAPRating(formData);
      setSapRating(rating);

      // Generate XML
      const xml = generateEPCXML(formData, rating);
      setGeneratedXML(xml);

      // Complete the process
      onComplete(formData, xml);
    } catch (error) {
      console.error('EPC generation failed:', error);
      setValidationErrors(['Failed to generate EPC. Please check your data and try again.']);
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderAssessorDetails();
      case 1:
        return renderPropertyDetails();
      case 2:
        return renderHeatingSystems();
      case 3:
        return renderBuildingFabric();
      case 4:
        return renderVentilation();
      case 5:
        return renderReviewAndGenerate();
      default:
        return <div>Unknown step</div>;
    }
  };

  const renderAssessorDetails = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <Award className="text-yellow-600" size={24} />
        Assessor & Report Details
      </h3>
      
      {/* Auto-population notice */}
      {currentUser?.user_metadata && (formData.assessorName || formData.assessorNumber || formData.assessorCompany) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-600" size={16} />
            <span className="text-sm font-medium text-green-800">
              Assessor details auto-populated from your settings
            </span>
          </div>
          <p className="text-xs text-green-700 mt-1">
            You can update these details in Settings → EPC Assessor Details
          </p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <span className="text-red-500">*</span> DEA Name
          </label>
          <input
            type="text"
            value={formData.assessorName}
            onChange={(e) => handleInputChange('assessorName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            placeholder="Enter certified assessor name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <span className="text-red-500">*</span> DEA Registration Number
          </label>
          <input
            type="text"
            value={formData.assessorNumber}
            onChange={(e) => handleInputChange('assessorNumber', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            placeholder="e.g., STRO123456"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            DEA Company
          </label>
          <input
            type="text"
            value={formData.assessorCompany}
            onChange={(e) => handleInputChange('assessorCompany', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            placeholder="Energy assessment company"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <span className="text-red-500">*</span> Assessment Date
          </label>
          <input
            type="date"
            value={formData.assessmentDate}
            onChange={(e) => handleInputChange('assessmentDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transaction Type
          </label>
          <select
            value={formData.transactionType}
            onChange={(e) => handleInputChange('transactionType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="1">Marketing to Owner-Occupier</option>
            <option value="2">Marketing to Tenant</option>
            <option value="8">Rental</option>
            <option value="14">Stock condition survey</option>
            <option value="5">None of the above</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tenure
          </label>
          <select
            value={formData.tenure}
            onChange={(e) => handleInputChange('tenure', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="1">Owner-occupied</option>
            <option value="2">Rented (social)</option>
            <option value="3">Rented (private)</option>
            <option value="ND">Unknown</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderPropertyDetails = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <Building className="text-yellow-600" size={24} />
        Property Identification & Details
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            UPRN (Unique Property Reference Number)
          </label>
          <input
            type="text"
            value={formData.uprn}
            onChange={(e) => handleInputChange('uprn', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            placeholder="Enter UPRN if available"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <span className="text-red-500">*</span> Property Address
          </label>
          <input
            type="text"
            value={formData.address.line1}
            onChange={(e) => handleNestedInputChange('address', 'line1', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 mb-2"
            placeholder="Address Line 1"
          />
          <input
            type="text"
            value={formData.address.line2 || ''}
            onChange={(e) => handleNestedInputChange('address', 'line2', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 mb-2"
            placeholder="Address Line 2 (optional)"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={formData.address.town}
              onChange={(e) => handleNestedInputChange('address', 'town', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              placeholder="Town"
            />
            <input
              type="text"
              value={formData.address.postcode}
              onChange={(e) => handleNestedInputChange('address', 'postcode', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              placeholder="Postcode"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Built Form
          </label>
          <select
            value={formData.builtForm}
            onChange={(e) => handleInputChange('builtForm', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="1">Detached</option>
            <option value="2">Semi-detached</option>
            <option value="3">End-terrace</option>
            <option value="4">Mid-terrace</option>
            <option value="5">Enclosed end-terrace</option>
            <option value="6">Enclosed mid-terrace</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property Type
          </label>
          <select
            value={formData.propertyType}
            onChange={(e) => handleInputChange('propertyType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="0">House</option>
            <option value="1">Flat</option>
            <option value="2">Maisonette</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Habitable Room Count
          </label>
          <input
            type="number"
            value={formData.habitableRoomCount}
            onChange={(e) => handleInputChange('habitableRoomCount', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            min="0"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Heated Room Count
          </label>
          <input
            type="number"
            value={formData.heatedRoomCount}
            onChange={(e) => handleInputChange('heatedRoomCount', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            min="0"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Extensions Count
          </label>
          <input
            type="number"
            value={formData.extensionsCount}
            onChange={(e) => handleInputChange('extensionsCount', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            min="0"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Measurement Type
          </label>
          <select
            value={formData.measurementType}
            onChange={(e) => handleInputChange('measurementType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="1">Internal</option>
            <option value="2">External</option>
            <option value="3">Approximate</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderHeatingSystems = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <Database className="text-yellow-600" size={24} />
        Heating Systems (PCDB Integration)
      </h3>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 mb-4">
          Select the main heating system from the official PCDB database. This ensures accurate efficiency ratings and compliance with SAP methodology.
        </p>
        
        <PCDBSearch
          onSelect={(system) => handleInputChange('mainHeatingSystem', system)}
          selectedSystem={formData.mainHeatingSystem}
          placeholder="Search PCDB for heating system by brand, model, or GC number..."
          className="w-full"
        />
      </div>
      
      {formData.mainHeatingSystem && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">Selected Heating System</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="font-medium">Brand:</span>
            <span>{formData.mainHeatingSystem.brand}</span>
            <span className="font-medium">Model:</span>
            <span>{formData.mainHeatingSystem.model}</span>
            <span className="font-medium">Type:</span>
            <span>{formData.mainHeatingSystem.type}</span>
            <span className="font-medium">Fuel:</span>
            <span>{formData.mainHeatingSystem.fuel}</span>
            <span className="font-medium">Efficiency:</span>
            <span>{formData.mainHeatingSystem.sap_winter_seasonal_efficiency}%</span>
            <span className="font-medium">PCDB Reference:</span>
            <span>{formData.mainHeatingSystem.pcdb_reference}</span>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Secondary Heating Type
          </label>
          <select
            value={formData.secondaryHeatingType}
            onChange={(e) => handleInputChange('secondaryHeatingType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="693">None</option>
            <option value="694">Room heater - mains gas</option>
            <option value="695">Room heater - LPG</option>
            <option value="696">Room heater - oil</option>
            <option value="697">Room heater - solid fuel</option>
            <option value="698">Room heater - electric</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hot Water Type
          </label>
          <select
            value={formData.hotWaterType}
            onChange={(e) => handleInputChange('hotWaterType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="901">From main system</option>
            <option value="902">Electric immersion</option>
            <option value="903">Gas multipoint</option>
            <option value="904">Electric multipoint</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Solar Water Heating
          </label>
          <select
            value={formData.solarWaterHeating.toString()}
            onChange={(e) => handleInputChange('solarWaterHeating', e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderBuildingFabric = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <Building className="text-yellow-600" size={24} />
        Building Fabric
      </h3>
      
      <p className="text-gray-600">
        Building fabric data will be auto-populated from your floor plan measurements and site notes.
        You can add additional elements or modify values below.
      </p>
      
      {/* Walls, Floors, Roofs, Windows sections would go here */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">Building Fabric Auto-Population</h4>
          <p className="text-sm text-gray-600">
            Wall areas, window dimensions, and floor areas will be calculated from your floor plan.
            U-values will be estimated based on construction type and age band from site notes.
          </p>
        </div>
      </div>
    </div>
  );

  const renderVentilation = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <FileText className="text-yellow-600" size={24} />
        Ventilation & Air Permeability
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pressure Test
          </label>
          <select
            value={formData.pressureTest}
            onChange={(e) => handleInputChange('pressureTest', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="1">Yes</option>
            <option value="2">Assumed</option>
            <option value="3">No</option>
          </select>
        </div>
        
        {formData.pressureTest !== '3' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Air Permeability (m³/h.m² @ 50Pa)
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.airPermeability || ''}
              onChange={(e) => handleInputChange('airPermeability', parseFloat(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            />
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mechanical Ventilation
          </label>
          <select
            value={formData.mechanicalVentilation}
            onChange={(e) => handleInputChange('mechanicalVentilation', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="0">Natural ventilation only</option>
            <option value="1">MEV centralised</option>
            <option value="2">MEV decentralised</option>
            <option value="3">MV</option>
            <option value="4">MVHR</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Extract Fans Count
          </label>
          <input
            type="number"
            value={formData.extractFansCount}
            onChange={(e) => handleInputChange('extractFansCount', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            min="0"
          />
        </div>
      </div>
    </div>
  );

  const renderReviewAndGenerate = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <Calculator className="text-yellow-600" size={24} />
        Review & Generate EPC
      </h3>
      
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="text-red-600" size={20} />
            <h4 className="font-semibold text-red-800">Validation Errors</h4>
          </div>
          <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">Data Summary</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="font-medium">Assessor:</span>
          <span>{formData.assessorName}</span>
          <span className="font-medium">Property:</span>
          <span>{formData.address.line1}</span>
          <span className="font-medium">Property Type:</span>
          <span>{formData.propertyType === '0' ? 'House' : 'Flat'}</span>
          <span className="font-medium">Heating System:</span>
          <span>{formData.mainHeatingSystem?.brand} {formData.mainHeatingSystem?.model}</span>
        </div>
      </div>
      
      {sapRating && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">SAP Calculation Results</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="font-medium">Current SAP Rating:</span>
            <span className="font-bold text-lg">{sapRating.currentRating}</span>
            <span className="font-medium">Potential SAP Rating:</span>
            <span className="font-bold text-lg">{sapRating.potentialRating}</span>
            <span className="font-medium">Current EI Rating:</span>
            <span>{sapRating.currentEI}</span>
            <span className="font-medium">Potential EI Rating:</span>
            <span>{sapRating.potentialEI}</span>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <button
          onClick={generateEPC}
          disabled={isGenerating}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Generating EPC...
            </>
          ) : (
            <>
              <Calculator size={20} />
              Generate Official EPC
            </>
          )}
        </button>
        
        {generatedXML && (
          <button
            onClick={() => {
              const blob = new Blob([generatedXML], { type: 'application/xml' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `EPC-${formData.address.postcode}-${formData.assessmentDate}.xml`;
              a.click();
            }}
            className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600"
          >
            <Download size={20} />
            Download XML
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-yellow-500 text-white' :
                    isCompleted ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {isCompleted ? <CheckCircle2 size={20} /> : <StepIcon size={20} />}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'font-semibold' : ''}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    isCompleted ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm min-h-[500px]">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={handlePreviousStep}
          disabled={currentStep === 0}
          className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
        >
          Previous
        </button>
        
        {currentStep < steps.length - 1 ? (
          <button
            onClick={handleNextStep}
            className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600"
          >
            Next
            <ChevronRight size={20} />
          </button>
        ) : (
          <div /> // Placeholder for final step
        )}
      </div>
    </div>
  );
};

export default EPCDataCollection; 